import { useState, useCallback, useRef, useEffect } from 'react';
import { IconPlus, IconX } from '@tabler/icons-react';
import { Text, Input, ErrorMsg } from '@fragments';
import QuestionCard from '../QuestionCard';
import { QUESTION_TYPES } from '../../index';


function getDefaultSettings(type) {
    if (type === 'select') return { options: [{ id: `opt-${Date.now()}`, label: '' }] };
    if (type === 'choices') return { options: [{ id: `opt-${Date.now()}`, label: '' }] };
    if (type === 'scale') return { min: 1, max: 5, minLabel: '', maxLabel: '' };
    if (type === 'yes_no_maybe') return { showMaybe: false };
    return null;
}

export default function StepConfig({
    selectedStep, stepNumber, updateStep,
    addQuestion, updateQuestion, removeQuestion, reorderQuestions,
    onDeselect,
    stepErrors, clearError,
    scrollSignal,
}) {
    const [addOpen, setAddOpen] = useState(false);
    const [expandedQuestionId, setExpandedQuestionId] = useState(null);
    const [draggingCardId, setDraggingCardId] = useState(null);
    const [liveQuestions, setLiveQuestions] = useState(null);

    const [localTitle, setLocalTitle] = useState('');
    const [localSubmitText, setLocalSubmitText] = useState('');

    const titleTimerRef = useRef(null);
    const bodyRef = useRef(null);

    useEffect(() => {
        setLocalTitle(selectedStep?.title ?? '');
        setLocalSubmitText(selectedStep?.submitButtonText ?? 'Continuar');

        setAddOpen(false);
        setExpandedQuestionId(null);
        setDraggingCardId(null);
        setLiveQuestions(null);
    }, [selectedStep?.id]);

    const handleTitleChange = useCallback((value) => {
        setLocalTitle(value);
        clearTimeout(titleTimerRef.current);
        titleTimerRef.current = setTimeout(() => {
            updateStep(selectedStep.id, { title: value });
        }, 350);
        clearError?.(['steps', selectedStep.id, 'title']);
    }, [selectedStep?.id, updateStep, clearError]);

    const handleSubmitTextChange = useCallback((value) => {
        setLocalSubmitText(value);
        updateStep(selectedStep.id, { submitButtonText: value });
    }, [selectedStep?.id, updateStep]);


    const handleSelectType = useCallback((type) => {
        const qt = QUESTION_TYPES.find(t => t.type === type);
        const newId = addQuestion(selectedStep.id, {
            type,
            label: qt?.defaultLabel ?? '',
            required: true,
            settings: getDefaultSettings(type),
        });
        setExpandedQuestionId(newId);
        setAddOpen(false);
    }, [selectedStep?.id, addQuestion]);

    useEffect(() => {
        if (!scrollSignal || !bodyRef.current) return;
        const id = setTimeout(() => {
            const field = bodyRef.current?.querySelector('[data-field-error="true"]');
            if (!field) return;
            field.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            field.querySelector('input, [contenteditable="true"]')?.focus();
        }, 80);
        return () => clearTimeout(id);
    }, [scrollSignal]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-expand first question with an error so user sees it
    useEffect(() => {
        if (!stepErrors?.questions) return;
        const firstErrorQId = Object.keys(stepErrors.questions)[0];
        if (firstErrorQId && expandedQuestionId !== firstErrorQId) {
            setExpandedQuestionId(firstErrorQId);
        }
    }, [stepErrors]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="form-builder-right-panel">
            <div className="fbrp-header">
                <div className="fbrp-header-left">
                    <div className="fbrp-step-badge">
                        <Text.span contrast={6}>{stepNumber}</Text.span>
                    </div>
                    <Text.span contrast={8} weight={600}>{localTitle || 'Sem título'}</Text.span>
                </div>
                <button className="fbrp-close-btn" onClick={onDeselect} title="Fechar">
                    <IconX size={14} />
                </button>
            </div>

            <div className="fbrp-body" ref={bodyRef}>
                <div className="fbrp-section">
                    <div className="fbrp-section-label">
                        <Text.span contrast={4}>CONFIGURAÇÕES</Text.span>
                    </div>

                    <div className="fbrp-field" data-field-error={stepErrors?.title ? 'true' : undefined}>
                        <Input
                            value={localTitle}
                            onInput={handleTitleChange}
                            label="Título da etapa"
                            placeholder="Ex: Dados pessoais"
                            fullWidth
                            error={!!stepErrors?.title}
                        />
                        <ErrorMsg>{stepErrors?.title}</ErrorMsg>
                    </div>

                    <div className="fbrp-field">
                        <Input
                            value={localSubmitText}
                            onInput={handleSubmitTextChange}
                            label="Texto do botão"
                            placeholder="Continuar"
                            fullWidth
                        />
                    </div>
                </div>


                <div className="fbrp-section" data-field-error={stepErrors?.noQuestions ? 'true' : undefined}>
                    <div className="fbrp-section-label">
                        <Text.span contrast={4}>PERGUNTAS</Text.span>
                        {selectedStep.questions?.length > 0 && (
                            <Text.span contrast={4}>{selectedStep.questions.length}</Text.span>
                        )}
                    </div>

                    {selectedStep.questions?.length > 0 && (() => {
                        const displayQuestions = liveQuestions ?? selectedStep.questions;
                        return (
                            <div className="fbrp-questions-list">
                                {displayQuestions.map(q => (
                                    <QuestionCard
                                        key={q.id}
                                        question={q}
                                        stepId={selectedStep.id}
                                        expanded={expandedQuestionId === q.id}
                                        onToggle={() => setExpandedQuestionId(id => id === q.id ? null : q.id)}
                                        updateQuestion={updateQuestion}
                                        removeQuestion={removeQuestion}
                                        questionErrors={stepErrors?.questions?.[q.id] ?? {}}
                                        clearError={clearError}
                                        cardDragProps={{
                                            draggable: true,
                                            onDragStart: (e) => {
                                                setDraggingCardId(q.id);
                                                setLiveQuestions([...selectedStep.questions]);
                                                e.dataTransfer.effectAllowed = 'move';
                                            },
                                            onDragOver: (e) => {
                                                e.preventDefault();
                                                if (!draggingCardId || draggingCardId === q.id) return;
                                                setLiveQuestions(prev => {
                                                    const cur = prev ?? selectedStep.questions;
                                                    const from = cur.findIndex(x => x.id === draggingCardId);
                                                    const to = cur.findIndex(x => x.id === q.id);
                                                    if (from === -1 || to === -1) return cur;
                                                    const next = [...cur];
                                                    const [item] = next.splice(from, 1);
                                                    next.splice(to, 0, item);
                                                    return next;
                                                });
                                            },
                                            onDrop: (e) => {
                                                e.preventDefault();
                                                if (liveQuestions) reorderQuestions(selectedStep.id, liveQuestions);
                                                setDraggingCardId(null);
                                                setLiveQuestions(null);
                                            },
                                            onDragEnd: () => { setDraggingCardId(null); setLiveQuestions(null); },
                                            'data-dragging': draggingCardId === q.id ? 'true' : undefined,
                                        }}
                                    />
                                ))}
                            </div>
                        );
                    })()}

                    {!addOpen ? (
                        <>
                            <button className="fbrp-add-btn" onClick={() => setAddOpen(true)}>
                                <IconPlus size={13} />
                                <Text.span contrast={5}>Adicionar pergunta</Text.span>
                            </button>
                            <ErrorMsg>{stepErrors?.noQuestions}</ErrorMsg>
                        </>
                    ) : (
                        <div className="fbrp-add-question-panel">
                            <div className="fbrp-aqp-header">
                                <Text.span contrast={6} weight={500}>Tipo de pergunta</Text.span>
                                <button className="fbrp-q-delete" style={{ opacity: 1 }} onClick={() => setAddOpen(false)}>
                                    <IconX size={13} />
                                </button>
                            </div>

                            <div className="fbrp-type-grid">
                                {QUESTION_TYPES.filter(t => !t.hidden).map(({ type, label, Icon, comingSoon }) => (
                                    <button
                                        key={type}
                                        className={`fbrp-type-chip${comingSoon ? ' fbrp-type-chip--soon' : ''}`}
                                        onClick={() => !comingSoon && handleSelectType(type)}
                                        disabled={comingSoon}
                                        title={comingSoon ? 'Em breve' : undefined}
                                    >
                                        <Icon size={16} />
                                        <Text.span contrast={4}>{label}</Text.span>
                                        {comingSoon && <span className="fbrp-soon-tag">Em breve</span>}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
